import React, { useState, useEffect } from 'react';
import { scoringService, ScoringWeights, DEFAULT_WEIGHTS } from '../services/scoringService';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface WeightAdjusterProps {
  onWeightsChange?: (weights: ScoringWeights) => void;
}

const WeightDot: React.FC<{ className?: string }> = ({ className }) => (
  <span className={cn('inline-block w-2.5 h-2.5 rounded-full', className)} aria-hidden="true" />
);

const WEIGHT_STYLE = {
  freshness: { icon: <WeightDot className="bg-purple-400" />, color: 'text-purple-400' },
  specificity: { icon: <WeightDot className="bg-blue-400" />, color: 'text-blue-400' },
  personaFit: { icon: <WeightDot className="bg-green-400" />, color: 'text-green-400' },
  replyHook: { icon: <WeightDot className="bg-yellow-400" />, color: 'text-yellow-400' },
  readability: { icon: <WeightDot className="bg-pink-400" />, color: 'text-pink-400' },
  risk: { icon: <WeightDot className="bg-red-400" />, color: 'text-red-400' },
};

export const WeightAdjuster: React.FC<WeightAdjusterProps> = ({ onWeightsChange }) => {
  const { t } = useT();
  const [weights, setWeights] = useState<ScoringWeights>(scoringService.loadWeights());
  const [isOpen, setIsOpen] = useState(false);
  const [showInfo, setShowInfo] = useState<keyof ScoringWeights | null>(null);

  useEffect(() => {
    onWeightsChange?.(weights);
  }, [weights, onWeightsChange]);

  const handleWeightChange = (key: keyof ScoringWeights, value: number) => {
    const newWeights = { ...weights, [key]: value };
    setWeights(newWeights);
    scoringService.saveWeights(newWeights);
  };

  const handleReset = () => {
    setWeights(DEFAULT_WEIGHTS);
    scoringService.saveWeights(DEFAULT_WEIGHTS);
  };

  const totalWeight = Object.entries(weights)
    .filter(([key]) => key !== 'risk')
    .reduce((sum, [, val]) => sum + val, 0);

  const isCustom = JSON.stringify(weights) !== JSON.stringify(DEFAULT_WEIGHTS);

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all',
          isCustom
            ? 'bg-accent/10 border-accent text-accent'
            : 'bg-surface-2 border-border text-text-muted hover:border-brand'
        )}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v17"/><path d="M5 8l7-5 7 5"/><path d="M3 13l2-5h0l2 5"/><path d="M17 13l2-5h0l2 5"/><circle cx="5" cy="13" r="2"/><circle cx="19" cy="13" r="2"/></svg>
          <div className="text-left">
            <p className="text-sm font-medium">
              {isCustom ? t('weightAdjuster.customWeights') : t('weightAdjuster.defaultWeights')}
            </p>
            <p className="text-xs opacity-75">
              {t('weightAdjuster.description')}
            </p>
          </div>
        </div>
        <span className={cn(
          'text-sm transition-transform',
          isOpen && 'rotate-180'
        )}>
          ▼
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-20 mt-2 w-full max-h-[600px] overflow-y-auto bg-surface-2 border border-border rounded-lg shadow-xl">
          {/* Header */}
          <div className="sticky top-0 bg-surface-2 border-b border-border p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-text-primary">{t('weightAdjuster.title')}</h3>
              {isCustom && (
                <button
                  onClick={handleReset}
                  className="px-3 py-1 text-xs bg-danger/10 text-danger rounded hover:bg-danger/20"
                >
                  {t('weightAdjuster.resetButton')}
                </button>
              )}
            </div>
            <p className="text-xs text-text-muted">
              {t('weightAdjuster.totalLabel', { total: totalWeight.toFixed(2) })} {totalWeight !== 1.0 && t('weightAdjuster.normalizationNote')}
            </p>
          </div>

          {/* Sliders */}
          <div className="p-4 space-y-6">
            {(Object.entries(weights) as [keyof ScoringWeights, number][]).map(([key, value]) => {
              const style = WEIGHT_STYLE[key];
              const isRisk = key === 'risk';
              const displayValue = isRisk ? Math.abs(value) : value;
              const percentage = (displayValue * 100).toFixed(0);

              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-lg', style.color)}>{style.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-text-primary flex items-center gap-2">
                          {t(`weightAdjuster.metrics.${key}.label`)}
                          <button
                            onMouseEnter={() => setShowInfo(key)}
                            onMouseLeave={() => setShowInfo(null)}
                            className="text-text-muted hover:text-brand transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                          </button>
                        </p>
                        {showInfo === key && (
                          <p className="text-xs text-text-muted mt-1 max-w-xs">
                            {t(`weightAdjuster.metrics.${key}.description`)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn('text-sm font-bold tabular-nums', style.color)}>
                        {isRisk ? '-' : ''}{percentage}%
                      </span>
                    </div>
                  </div>

                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max={isRisk ? "0.5" : "1"}
                      step="0.01"
                      value={displayValue}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        handleWeightChange(key, isRisk ? -newValue : newValue);
                      }}
                      className={cn(
                        'w-full h-2 rounded-lg appearance-none cursor-pointer',
                        'bg-surface-1',
                        '[&::-webkit-slider-thumb]:appearance-none',
                        '[&::-webkit-slider-thumb]:w-4',
                        '[&::-webkit-slider-thumb]:h-4',
                        '[&::-webkit-slider-thumb]:rounded-full',
                        '[&::-webkit-slider-thumb]:bg-brand',
                        '[&::-webkit-slider-thumb]:cursor-pointer',
                        '[&::-moz-range-thumb]:w-4',
                        '[&::-moz-range-thumb]:h-4',
                        '[&::-moz-range-thumb]:rounded-full',
                        '[&::-moz-range-thumb]:bg-brand',
                        '[&::-moz-range-thumb]:border-0',
                        '[&::-moz-range-thumb]:cursor-pointer'
                      )}
                      style={{
                        background: `linear-gradient(to right, ${style.color.replace('text-', '')} 0%, ${style.color.replace('text-', '')} ${displayValue * 100}%, rgb(31, 35, 40) ${displayValue * 100}%, rgb(31, 35, 40) 100%)`
                      }}
                    />
                  </div>

                  {/* Quick Presets */}
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() => handleWeightChange(key, isRisk ? -0 : 0)}
                      className="px-2 py-1 bg-surface-1 rounded hover:bg-border transition-colors"
                    >
                      0%
                    </button>
                    <button
                      onClick={() => handleWeightChange(key, isRisk ? -0.25 : 0.25)}
                      className="px-2 py-1 bg-surface-1 rounded hover:bg-border transition-colors"
                    >
                      25%
                    </button>
                    <button
                      onClick={() => handleWeightChange(key, isRisk ? -0.5 : 0.5)}
                      className="px-2 py-1 bg-surface-1 rounded hover:bg-border transition-colors"
                    >
                      50%
                    </button>
                    {!isRisk && (
                      <>
                        <button
                          onClick={() => handleWeightChange(key, 0.75)}
                          className="px-2 py-1 bg-surface-1 rounded hover:bg-border transition-colors"
                        >
                          75%
                        </button>
                        <button
                          onClick={() => handleWeightChange(key, 1)}
                          className="px-2 py-1 bg-surface-1 rounded hover:bg-border transition-colors"
                        >
                          100%
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Presets */}
          <div className="border-t border-border p-4">
            <p className="text-sm font-medium text-text-primary mb-3">{t('weightAdjuster.presetsSection')}</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  const preset = { freshness: 0.4, specificity: 0.3, personaFit: 0.1, replyHook: 0.1, readability: 0.1, risk: -0.05 };
                  setWeights(preset);
                  scoringService.saveWeights(preset);
                }}
                className="p-3 bg-surface-1 border border-border rounded-lg text-left hover:border-brand transition-colors"
              >
                <p className="text-sm font-medium text-text-primary">{t('weightAdjuster.presets.buzz.title')}</p>
                <p className="text-xs text-text-muted">{t('weightAdjuster.presets.buzz.description')}</p>
              </button>
              <button
                onClick={() => {
                  const preset = { freshness: 0.1, specificity: 0.2, personaFit: 0.4, replyHook: 0.15, readability: 0.15, risk: -0.1 };
                  setWeights(preset);
                  scoringService.saveWeights(preset);
                }}
                className="p-3 bg-surface-1 border border-border rounded-lg text-left hover:border-brand transition-colors"
              >
                <p className="text-sm font-medium text-text-primary">{t('weightAdjuster.presets.style.title')}</p>
                <p className="text-xs text-text-muted">{t('weightAdjuster.presets.style.description')}</p>
              </button>
              <button
                onClick={() => {
                  const preset = { freshness: 0.15, specificity: 0.15, personaFit: 0.15, replyHook: 0.4, readability: 0.15, risk: -0.1 };
                  setWeights(preset);
                  scoringService.saveWeights(preset);
                }}
                className="p-3 bg-surface-1 border border-border rounded-lg text-left hover:border-brand transition-colors"
              >
                <p className="text-sm font-medium text-text-primary">{t('weightAdjuster.presets.engagement.title')}</p>
                <p className="text-xs text-text-muted">{t('weightAdjuster.presets.engagement.description')}</p>
              </button>
              <button
                onClick={() => {
                  const preset = { freshness: 0.2, specificity: 0.2, personaFit: 0.2, replyHook: 0.15, readability: 0.25, risk: -0.15 };
                  setWeights(preset);
                  scoringService.saveWeights(preset);
                }}
                className="p-3 bg-surface-1 border border-border rounded-lg text-left hover:border-brand transition-colors"
              >
                <p className="text-sm font-medium text-text-primary">{t('weightAdjuster.presets.safety.title')}</p>
                <p className="text-xs text-text-muted">{t('weightAdjuster.presets.safety.description')}</p>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-surface-2 border-t border-border p-3">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full py-2 bg-brand text-white rounded-lg hover:bg-brand/90"
            >
              {t('common.done')}
            </button>
          </div>
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
